import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../theme/Icon';
import { ApiError } from '@app/api/ApiError';
import { errorView } from '@app/errors';
import s from './ErrorBanner.module.css';
import { t } from '@app/i18n';

export interface ErrorBannerProps {
  /** O erro da API, ou só o `code`. */
  error: ApiError | string | null | undefined;
  /**
   * `true` quando a operação era de escrita: o banner acrescenta "Nada foi gravado."
   * (FR-123). Em falha parcial (`desfeito: false`) o sufixo não entra.
   */
  escrita?: boolean;
  /** Título do banner. Padrão: "Não foi possível concluir". */
  title?: string;
  /** "Tentar de novo" (FR-126). */
  onRetry?: () => void;
  retryLabel?: string;
  /** Sobrescreve o destino do link sugerido pela tabela de erros. */
  onFollowLink?: (rota: string) => void;
  /**
   * Esconde o link corretivo. Use quando a tela **é** o destino dele — mandar o
   * usuário para onde ele já está é ruído, não ajuda.
   */
  hideLink?: boolean;
  /**
   * Valores para os marcadores que o `ErroData` do contrato **não** carrega — hoje `{namespace}`,
   * `{slug}` e `{tool}`. Quem os conhece é a tela, não o erro: `deployment_inexistente` cita
   * «{namespace}/{slug}» e sem isto o banner mostraria o template cru (ou a frase degradada).
   */
  dados?: Record<string, string | undefined>;
  /** Conteúdo extra (lista de campos inválidos, por exemplo). */
  children?: ReactNode;
  className?: string;
}

/**
 * Banner de erro no topo do drawer/tela (FR-123): mensagem amigável +
 * código técnico entre parênteses + "Nada foi gravado.", mantendo o formulário.
 * Mostra os `passos[]` do middleware quando o envelope os traz.
 */
export function ErrorBanner({
  error,
  escrita = false,
  title = t('common.ui.actionFailed'),
  onRetry,
  retryLabel = t('common.ui.retry'),
  onFollowLink,
  hideLink = false,
  dados,
  children,
  className,
}: ErrorBannerProps) {
  if (!error) return null;

  const complete = errorView(error, { escrita, dados });
  const view = hideLink ? { ...complete, link: undefined } : complete;
  const apiError = error instanceof ApiError ? error : null;
  const passos = apiError?.passos ?? [];

  return (
    <div className={[s.alert, s.danger, className].filter(Boolean).join(' ')} role="alert">
      <Icon name="shield-alert" aria-hidden="true" />
      <div className={s.body}>
        <h4>{title}</h4>
        <p>
          {view.mensagem}
          {/* Código fora da tabela já é nomeado na própria frase ("O middleware
              respondeu «x»: …"); repeti-lo aqui poria o mesmo código duas vezes
              na mesma linha. */}
          {view.mostrarCodigo ? <span className={s.codigo}> ({view.code})</span> : null}
        </p>

        {passos.length ? (
          <ul className={s.passos}>
            {passos.map((step, i) => (
              <li className={s.passo} key={`${step.target}-${i}`}>
                <Icon name={step.ok ? 'check' : 'close'} size="xs" aria-hidden="true" />
                <span className={s.passoAlvo}>{step.target}</span>
                {step.detail ? <span>— {step.detail}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}

        {children}

        {view.requestId ? (
          // A chave que liga este banner ao registro de T23 e ao trace do
          // middleware. Sem ela, quem for investigar depois tem só "deu erro ao
          // aprovar, por volta das 15h".
          <p className={s.requestId}>
            request-id <span className={s.codigo}>{view.requestId}</span>
          </p>
        ) : null}

        {onRetry || view.link ? (
          <div className={s.actions}>
            {onRetry ? (
              <button type="button" className={s.link} onClick={onRetry}>
                {retryLabel}
              </button>
            ) : null}
            {view.link ? (
              onFollowLink ? (
                <button
                  type="button"
                  className={s.link}
                  onClick={() => onFollowLink(view.link!.rota)}
                >
                  {view.link.rotulo}
                </button>
              ) : (
                // `Link`, não `<a href>`: a ação corretiva é uma rota do próprio
                // console — um `href` cru recarregaria a aplicação inteira e o
                // usuário perderia o formulário que o erro mandou manter (FR-123).
                <Link className={s.link} to={view.link.rota}>
                  {view.link.rotulo}
                </Link>
              )
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export type AlertTone = 'info' | 'warning' | 'danger';

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  /** Corpo. Opcional desde 08/09: um toast de sucesso só precisa do título. */
  children?: ReactNode;
  className?: string;
  /**
   * Papel ARIA. Padrão: `note` para `info`, `alert` para o resto. `status` é
   * para o aviso **persistente** de estado (FR-125, `MiddlewareDownNotice`):
   * ele não é o erro de uma ação, e ocupar `role="alert"` faria todo seletor de
   * "banner de erro" (E2E, `codigoDoErro`) tropeçar nele.
   */
  role?: 'alert' | 'note' | 'status';
}

/** Aviso genérico (`.alert`) — ex.: "middleware fora" nas telas de escrita (FR-125). */
export function Alert({ tone = 'info', title, children, className, role }: AlertProps) {
  const icon = tone === 'danger' ? 'shield-alert' : tone === 'warning' ? 'warning' : 'info';
  return (
    <div
      className={[s.alert, s[tone], className].filter(Boolean).join(' ')}
      role={role ?? (tone === 'info' ? 'note' : 'alert')}
    >
      <Icon name={icon} aria-hidden="true" />
      <div className={s.body}>
        {title ? <h4>{title}</h4> : null}
        {typeof children === 'string' ? <p>{children}</p> : children}
      </div>
    </div>
  );
}
